import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Label,
  Row,
  Button,
  Table,
} from "reactstrap";
import Select from "react-select";
// Using custom Bootstrap pagination for consistency
import { getLeaveTypes } from "../../components/Api/GmsLeaveType";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    companyId: businessId,
  };
};

const defaultForm = {
  leaveType_NameEn: "",
  leaveType_NameAr: "",
  leaveType_FullPaidDays: 0,
  leaveType_HalfPaidDays: 0,
  leaveType_UnPaidDays: 0,
  leaveType_Comments: "",
  leaveType_Business_Id: null
};

const emptyFilter = {
  leaveType_NameEn: "",
  leaveType_NameAr: "",
  leaveType_Business_Id: null
};

const LeaveTypePage = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [nameEnOptions, setNameEnOptions] = useState([]);
  const [nameArOptions, setNameArOptions] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const session = useMemo(() => getUserSession(), []);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsLeaveType');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsLeaveType');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsLeaveType');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsLeaveType');
  const isAdmin = isUserAdmin();

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "serialNumber",
        enableColumnFilter: false,
        cell: ({ row }) => row.index + 1,
      },
      {
        header: "Name (En)",
        accessorKey: "leaveType_NameEn",
        enableColumnFilter: false,
      },
      {
        header: "Name (Ar)",
        accessorKey: "leaveType_NameAr",
        enableColumnFilter: false,
      },
      {
        header: "Full Paid Days",
        accessorKey: "leaveType_FullPaidDays",
        enableColumnFilter: false,
      },
      {
        header: "Half Paid Days",
        accessorKey: "leaveType_HalfPaidDays",
        enableColumnFilter: false,
      },
      {
        header: "Unpaid Days",
        accessorKey: "leaveType_UnPaidDays",
        enableColumnFilter: false,
      }
    ],
    []
  );

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadLeaveTypes();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate business in filter if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !filter.leaveType_Business_Id) {
      const business = businesses.find(b => b.business_Id === session.businessId);
      if (business) {
        setFilter(prev => ({
          ...prev,
          leaveType_Business_Id: session.businessId
        }));
      }
    }
  }, [businesses, session.businessId]);

  useEffect(() => {
    if (leaveTypes.length > 0) {
      // Create options for dropdowns from the loaded data
      const enOptions = [...new Set(leaveTypes.map(lt => lt.leaveType_NameEn))]
        .map(name => ({ value: name, label: name }));
      const arOptions = [...new Set(leaveTypes.map(lt => lt.leaveType_NameAr))]
        .map(name => ({ value: name, label: name }));

      setNameEnOptions(enOptions);
      setNameArOptions(arOptions);
    }
  }, [leaveTypes]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const businessOptions = data.map(b => ({
        value: b.business_Id,
        label: b.business_NameEn || b.business_Name
      }));
      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadLeaveTypes = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getLeaveTypes(filterData);
      setLeaveTypes(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load leave types: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, selectedOption) => {
    setFilter(prev => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : (field === 'leaveType_Business_Id' ? null : "")
    }));
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadLeaveTypes(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadLeaveTypes(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Get paginated data for display
  const getPaginatedLeaveTypes = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return leaveTypes.slice(startIndex, endIndex);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4 className="card-title mb-0">Leave Types</h4>
                  <Button
                    color="primary"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    {showFilters ? "Hide Filters" : "Show Filters"}
                  </Button>
                </div>

                {showFilters && (
                  <Form className="mb-4">
                    <Row>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label>Company</Label>
                          <Select
                            value={businesses.find(b => b.value === filter.leaveType_Business_Id) || null}
                            onChange={(option) => handleFilterChange("leaveType_Business_Id", option)}
                            options={businesses}
                            isClearable={!session.businessId}
                            isDisabled={!!session.businessId}
                            isSearchable
                            placeholder="Select Company"
                            className="react-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label>Name (English)</Label>
                          <Select
                            value={filter.leaveType_NameEn ? { value: filter.leaveType_NameEn, label: filter.leaveType_NameEn } : null}
                            onChange={(option) => handleFilterChange("leaveType_NameEn", option)}
                            options={nameEnOptions}
                            isClearable
                            isSearchable
                            placeholder="Select or type to search..."
                            className="react-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="mb-3">
                          <Label>Name (Arabic)</Label>
                          <Select
                            value={filter.leaveType_NameAr ? { value: filter.leaveType_NameAr, label: filter.leaveType_NameAr } : null}
                            onChange={(option) => handleFilterChange("leaveType_NameAr", option)}
                            options={nameArOptions}
                            isClearable
                            isSearchable
                            placeholder="Select or type to search..."
                            className="react-select"
                            classNamePrefix="select"
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12} className="d-flex align-items-end">
                        <div className="mb-3 d-flex gap-2">
                          <Button color="primary" onClick={handleApplyFilter}>
                            Apply Filter
                          </Button>
                          <Button color="secondary" onClick={handleClearFilter}>
                            Clear Filter
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}

                <div className="table-responsive">
                  <Table bordered>
                    <thead className="table-light">
                      <tr>
                        <th>Sr.No</th>
                        <th>Name (EN)</th>
                        <th>Name (AR)</th>
                        <th>Full Paid Days</th>
                        <th>Half Paid Days</th>
                        <th>Unpaid Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveTypes.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            {loading ? "Loading..." : "No leave types found"}
                          </td>
                        </tr>
                      ) : (
                        getPaginatedLeaveTypes().map((leaveType, index) => (
                          <tr key={leaveType.leaveType_Id}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>{leaveType.leaveType_NameEn}</td>
                            <td>{leaveType.leaveType_NameAr}</td>
                            <td>{leaveType.leaveType_FullPaidDays}</td>
                            <td>{leaveType.leaveType_HalfPaidDays}</td>
                            <td>{leaveType.leaveType_UnPaidDays}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4 p-3"
                  style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
                  <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                    <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                    Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
                  </div>
                  {totalCount > pageSize && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Button
                        color="light"
                        size="sm"
                        disabled={page === 1}
                        onClick={() => handlePageChange(page - 1)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                          color: page === 1 ? '#6c757d' : '#495057'
                        }}
                      >
                        <i className="mdi mdi-chevron-left"></i>
                      </Button>

                      {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                        const pageNum = index + 1;
                        const isActive = page === pageNum;

                        return (
                          <Button
                            key={pageNum}
                            color={isActive ? "primary" : "light"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '0.375rem',
                              minWidth: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: isActive ? '#556ee6' : '#fff',
                              borderColor: isActive ? '#556ee6' : '#dee2e6',
                              color: isActive ? '#fff' : '#495057',
                              fontWeight: isActive ? '600' : '400',
                              boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                            }}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}

                      <Button
                        color="light"
                        size="sm"
                        disabled={page === Math.ceil(totalCount / pageSize)}
                        onClick={() => handlePageChange(page + 1)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                          color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                        }}
                      >
                        <i className="mdi mdi-chevron-right"></i>
                      </Button>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LeaveTypePage; 